import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { TransitionPresence, useScrollTransition } from 'util/react-transition-component';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import ServicePowerSlideBackground from './service-power-slide-background/ServicePowerSlideBackground';
import type { ServicePowerSlideBackgroundRef } from './service-power-slide-background/ServicePowerSlideBackground.data';
import ServicePowerSlide from './service-power-slide/ServicePowerSlide';
import type { ServicePowerSlideRef } from './service-power-slide/ServicePowerSlide.data';
import ServicePowerWebgl from './service-power-webgl/ServicePowerWebgl';
import type { ServicePowerWebglRef } from './service-power-webgl/ServicePowerWebgl.data';
import type { AnimalType, ServicePowersProps, ServicePowersRef } from './ServicePowers.data';
import { servicePowerConfig } from './ServicePowers.data';
import styles from './ServicePowers.module.scss';
import { setupTransitionInTimeline } from './ServicePowers.transitions';

export default function ServicePowers({
  blok,
  ref,
  ...props
}: ServicePowersProps & { ref?: Ref<ServicePowersRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const keyPrefix = blok._uid;
  const [activeSlide, setActiveSlide] = useState(-1);
  const webglRef = useRef<ServicePowerWebglRef>(null);
  const slideBackgroundRef = useRef<ServicePowerSlideBackgroundRef>(null);
  const powerSlideRef = useRef<ServicePowerSlideRef>(null);

  const progressData = useRef<Array<{ id: AnimalType; progress: number }>>(
    blok.slides.map((slide) => ({
      id: slide.animal_type,
      progress: 0,
    })),
  );

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  useScrollTrigger({
    trigger: elementRef,
    endTrigger: wrapperRef,
    pin: wrapperRef,
    vars: {
      start: 'top top',
      end: `bottom bottom-=${blok.slides.length * 100 * 4}%`,
      pinType: 'transform',
      anticipatePin: 1,
      scrub: true,
      onUpdate: ({ progress }) => {
        const totalSlides = blok.slides.length;
        const slideDuration = 1 / totalSlides;
        let slideIndex = Math.max(-1, progress > 0 ? Math.floor(progress / slideDuration) : -1);

        if (slideIndex > totalSlides - 1) {
          slideIndex = -1;
        }

        const slideProgress = (progress - slideIndex * slideDuration) / slideDuration;
        // setActiveSlide(slideIndex);
        if (activeSlide !== slideIndex) {
          setActiveSlide(slideIndex);
          if (webglRef?.current) webglRef.current.setCurrentSlide(slideIndex);
        }

        if (slideIndex > -1) {
          progressData.current[slideIndex].progress = slideProgress;
          if (webglRef?.current) webglRef.current.setProgressData(progressData.current);
          if (slideBackgroundRef?.current)
            slideBackgroundRef.current.setProgress(slideProgress, slideIndex);
          if (powerSlideRef?.current) powerSlideRef.current.setProgress(slideProgress, slideIndex);
        }

        if (webglRef.current) webglRef.current.setProgress(progress);
      },
    },
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.servicePowers, props.className)}
      {...sbEditable(blok)}
    >
      <div ref={wrapperRef} className={classNames(styles.wrapper)}>
        <div className={classNames(styles.slideBackgroundHolder)}>
          <TransitionPresence>
            {activeSlide >= 0 && (
              <ServicePowerSlideBackground
                ref={slideBackgroundRef}
                key={`${keyPrefix}_slide_bg_${blok.slides[activeSlide]._uid}`}
                blok={blok.slides[activeSlide]}
                config={servicePowerConfig[blok.slides[activeSlide].animal_type]}
                className={classNames(styles.backgroundSlide)}
                slideIndex={activeSlide}
                transitionOnMount
                transitionOnUnMount
              />
            )}
          </TransitionPresence>
        </div>

        <div className={classNames(styles.webglContainer)}>
          <ServicePowerWebgl
            ref={webglRef}
            slides={blok.slides.map((slide) => ({
              id: slide.animal_type,
              background_title: slide.background_title,
              ...servicePowerConfig[slide.animal_type],
            }))}
          />
        </div>

        <div className={classNames(styles.slideHolder)}>
          <TransitionPresence>
            {activeSlide >= 0 && (
              <ServicePowerSlide
                ref={powerSlideRef}
                // <TransitionPresence>
                //   {blok.slides.map((slide, index:number) => (
                //     (index === activeSlide) && <ServicePowerSlide
                //       key={`${keyPrefix}_slide_${slide._uid}`}
                //       blok={slide}
                key={`${keyPrefix}_slide_${blok.slides[activeSlide]._uid}`}
                blok={blok.slides[activeSlide]}
                config={servicePowerConfig[blok.slides[activeSlide].animal_type]}
                className={classNames(styles.slide)}
                slideIndex={activeSlide}
                transitionOnMount
                transitionOnUnMount
                // isVisible={{
                //   onMount: true,
                //   value: index === activeSlide,
                // }}
              />
              // ))}
            )}
          </TransitionPresence>
        </div>
      </div>
    </div>
  );
}
